import { InputNumberLocalized } from '@components'

import { Flex, Spacing, Text } from '@billy/design-system'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useDebouncedCallback } from '../../../../../../../../../hooks/useDebouncedCallback'

const DEBOUNCE_DELAY = 500

interface AmountRangeFilterProps {
  minAmount?: number
  maxAmount?: number
  currency?: string
  onMinChange: (value: number) => void
  onMaxChange: (value: number) => void
}

export const AmountRangeFilter = ({
  minAmount,
  maxAmount,
  currency,
  onMinChange,
  onMaxChange,
}: AmountRangeFilterProps) => {
  const { t } = useTranslation()
  const handleMinAmountChange = useDebouncedCallback((value: number) => onMinChange(value), DEBOUNCE_DELAY)
  const handleMaxAmountChange = useDebouncedCallback((value: number) => onMaxChange(value), DEBOUNCE_DELAY)

  const amountFields = useMemo(
    () => [
      { key: 'min', placeholder: '0', value: minAmount, onChange: handleMinAmountChange },
      { key: 'max', placeholder: '1000000', value: maxAmount, onChange: handleMaxAmountChange },
    ],
    [minAmount, maxAmount, handleMinAmountChange, handleMaxAmountChange],
  )

  return (
    <>
      <Text colorVariant="discreet" weight="semiBold">
        {t('bank_transactions.filter.amount')}
      </Text>
      <Flex alignItems="center" gap={Spacing.S8}>
        {amountFields.map(({ key, placeholder, value, onChange }, index) => (
          <Fragment key={key}>
            {index > 0 && <Text colorVariant="secondary">{t('bank_transactions.filter.amount_separator')}</Text>}
            <InputNumberLocalized
              key={`${key}-${value === undefined}`}
              size="sm"
              min={0}
              isFallbackToZero={false}
              placeholder={placeholder}
              suffix={currency}
              value={value}
              onChange={onChange}
            />
          </Fragment>
        ))}
      </Flex>
    </>
  )
}
